import React, { useState, useEffect } from 'react';
import { CBAssetCatalog, CBContentPreviewsModal, CBPage, CBHeader, CBHeaderButton, CBAssetImage, CBInstitutionPreview, CBSavedListPreview, CBMessagePreview, CBMessageFeedPreview } from 'cb-message-builder'
import 'cb-message-builder/dist/index.css'

import assetTemplates from "./AssetTemplates.js"
import {
    createBrowserRouter, RouterProvider, useNavigate, useParams, useLoaderData, ScrollRestoration, Link
} from "react-router-dom";

import { loader, indexLoader, fileUploader, updateAsset, deleteAsset, loadLibraryAssets, deleteLibraryAsset, updateLibraryAsset } from "./AppData.js"



/*
 * Asset Catalogue Page
 */
const AppAssetCatalog = ({ catalogAssets: initialCatalogAssets, institutionid }) => {

    const [catalogAssets, setCatalogAssets] = useState(initialCatalogAssets);
    const [assetLibrary, setAssetLibrary] = useState([]);
    const [showPreviews, setShowPreviews] = useState(false);

    /**
     * Load the global Asset Library
     * 
     * This will be used to allow the user to select assets from and to add new ones to.
     */
    useEffect( () => {
        loadLibraryAssets().then(
                (value) => {
                    setAssetLibrary(value)            
                },
                (error) => {
                    console.log(error)
                }
        )
    }, [])


    // Test disabled support
    let assetIdTest = 'ef27c4c7-22d4-4797-be6b-18cbddc3cf6a'
    if (assetLibrary[assetIdTest]) {
        assetLibrary[assetIdTest].disabled = true;
        assetLibrary[assetIdTest].disabledReason = 'You cannot choose an image with this cat in it.';
    }


    /**
     * Asset Catalog Item Callbacks
     * 
     * These callbacks deal with modifying the catalog contents
     */

    // When an asset in the catalog is cleared / deleted
    const onAssetDelete = (asset) => {
        setCatalogAssets({...deleteAsset(asset, institutionid)});
    }

    // When an asset in the catalog is updated 
    const onAssetChange = (asset) => {
        console.log('onAssetChange')
        setCatalogAssets({...updateAsset(asset, institutionid)});
    }

    /**
     * Asset Library Callbacks
     * 
     * These callbacks deal with any actions done to the global asset library
     */

    // When a file is deleted from the asset library
    const onAssetLibraryDelete = (libraryAsset) => {
        console.log('onAssetLibraryDelete')
        setAssetLibrary({...deleteLibraryAsset(libraryAsset, 'test')})
    }
    
    // When a file is updated from the asset library
    const onAssetLibraryChange = (libraryAsset) => {
        console.log('onAssetLibraryChange')
        console.log(libraryAsset)
        setAssetLibrary({...updateLibraryAsset(libraryAsset, 'test')})
    }

    // onAssetLibraryImport callback
    //
    // Passes in the follow parameters 
    // - file: HTML File object that represents the file selected by the user
    // - asset: Suggested dictionary for convenience to represent the asset object. Will contain id, type, title, dimensions.
    // - album: Album the user is currently in 
    // - progressCallback: Callback to call when upload progress changes. progressCallback(percentComplete);
    // - completeCallback: Callback to call when upload is complete. completeCallback(error)
    // - errorCallback: Callback to call if theres an error errorCallback(message)
    //
    // Return value:
    //   Function to call that will be called if the upload is cancelled by the user
    //
    const onAssetLibraryImport = (file, asset, album, progressCallback, completeCallback, errorCallback) => {       

        const uploadTask = fileUploader({
            institutionId: institutionid,
            file: file,
            asset: asset,
            progressCallback: progressCallback,
            completeCallback: completeCallback,
            errorCallback: errorCallback,
            setAssets: setAssetLibrary
        });

        // Return value to onAssetImport is a function that will be called if the upload is cancelled by the user
        return () => {
            uploadTask.cancel()
        }

    }

    // convert firebase collection to array to use as the asset library source
    var assetLibraryData = []
    for (const assetKey in assetLibrary) {
        const asset = assetLibrary[assetKey];
        assetLibraryData.push(asset)
    }

    // Some demo data
    var institutionTitle = "Some University"
    var institutionLocation = "Somewhere, Someplace" 

    /*
     * Preview Callback
     *
     * Returns appropriate preview view for a specific preview id.
     * This is implemented as a callback so can easily customise preview content based on institution data.
     */
    const preview = (id, assetId) =>     {

        // Return the asset object for the requested assetId
        const assetSrc = (assetId) => {
            return catalogAssets[assetId]
        }

        // Current ids are:
        //
        // * institution-profile - The profile for an college
        // * saved               - The saved tab that shows a college tile and logo
        // * message             - A message
        // * message-feed        - The message feed tab that shows a college word mark.
        // * message-feed-dark   - Dark version of message-feed
        //
        switch (id) {
            
            /** 
             * Preview - College Profile Screen 
             */
            case 'institution-profile':
                return <CBInstitutionPreview name="College Profile"
                                             altText={"College profile screen that shows the logo centered in the header"}
                                             title={institutionTitle}
                                             subtitle={institutionLocation}
                                             background={assetSrc('header')}
                                             logo={assetSrc('logo-avatar')}/>

            /** 
             * Preview - Saved Screen
             */
            case 'saved':
                // College Tiles
                let tiles = [
                    {
                        'title'      : 'University Title',
                        'logo'       : assetSrc('logo-avatar'),
                        'background' : assetSrc('header')
                    },
                    { 
                        'title' : 'Other University' 
                    }
                ]

                // Messages
                let listItems = [
                    { 'title' : 'Lorem Ipsum', image: assetSrc('logo-avatar'), logo: assetSrc('logo-avatar') }, // Show an empty message 
                    { 'title' : '' },
                ]

                return <CBSavedListPreview  name="Saved Screen"
                                            tilesHeader={"Connections"}
                                            tileItems={tiles}
                                            listItems={listItems}/>

            /** 
             * Preview - Message
             */
            case 'message':
                return <CBMessagePreview 
                            altText={"Message that shows the square logo below the image to the left of the institution details"}
                            name="Message"
                            title={"Message Title"} 
                            caption={"TUE 13 JUN, 6:21 PM"}
                            institutionTitle={institutionTitle}
                            institutionLocation={institutionLocation}
                            institutionLogo={assetSrc('logo-avatar')}
                            message={"This is a message!"}/>

            /** 
             * Preview - Messages Feed. 
             * 
             * Can be light & dark.
             */
            case 'message-feed':
            case 'message-feed-dark':

                // Choose logo type for message card based on asset the preview is for.
                // If null (when viewing all previews) it'll be selected based on what assets are available
                var logoType = null
                switch (assetId) {
                    case 'logo-avatar':
                        logoType = 'avatar'
                        break
                    case 'wordmark-logo-light':
                    case 'wordmark-logo-dark':
                        logoType = 'wordmark'
                        break
                }

                let messageItems = [
                    {
                        'type'       : 'message-card',
                        'title'      : 'Lorem ipsum dolor sit amet, consectetur adipiscing elit...',
                        'caption'    : 'OCT 10',
                        'logo'       : assetSrc('logo-wordmark-' + ((id == "message-feed-dark") ? "dark" : "light") ),
                        'logoAvatar' : assetSrc('logo-avatar'),
                        'logoType'   : logoType,
                        'institutionTitle' : institutionTitle
                    },
                    {   'title' : '' }, // Show an empty message
                    {   'title' : '' },
                    {   'title' : '' }               
                ]
    
                return <CBMessageFeedPreview 
                            altText={"Message feed that shows the square logo inside the preview of a message card"}
                            name={id == "message-feed-dark" ? "Message Feed Dark" : "Message Feed"}
                            dark={id == "message-feed-dark"}
                            title={"Messages"} 
                            messages={messageItems}/>             
        }
        return null
    }

    let previewButton = <CBHeaderButton primary={true} onClick={ () => { setShowPreviews(true)}}>View Previews</CBHeaderButton>

    return (
        /** A container class name can be passed or omitted to not set one */
        <CBPage containerClassName={"content-builder-container-inset"}>

            <CBHeader title={"Asset Catalog"} headerActions={[previewButton]}></CBHeader>

            <CBAssetCatalog assets={catalogAssets} 
                            assetTemplates={assetTemplates}
                            onAssetChange={onAssetChange} 
                            onAssetDelete={onAssetDelete} 
                            
                            /** Previews */
                            previewAssetPath={"/previews"}
                            previewCallback={preview}

                            /** Asset library */
                            assetLibrary={assetLibraryData}                            
                            onAssetLibraryDelete={onAssetLibraryDelete} 
                            onAssetLibraryImport={onAssetLibraryImport}
                            onAssetLibraryChange={onAssetLibraryChange}

                            /** Disable / enable cropping */
                            canCrop={true}

                            /** Disable / enable multi upload */
                            canMultiUpload={true}
                            />

            {/** Preview Modal Example */}
            <CBContentPreviewsModal show={showPreviews} onClose={setShowPreviews}>
                {preview('institution-profile')}
                {preview('message')}
                {preview('message-feed')}
                {preview('message-feed-dark')}
                {preview('saved')}
            </CBContentPreviewsModal>                        

        </CBPage>
    )
}


const AppAssetCatalogLoader = () => {
    const catalogAssets = useLoaderData()
    const { institutionid } = useParams();

    return <AppAssetCatalog catalogAssets={catalogAssets} institutionid={institutionid}/>
}

/*
 * Asset Demo Index Page
 */

// LinkComponent is meant to be a Link component from react-router-dom.
// When using this as a package , it didnt like using the one defined by this package, so passing externally.
const AppAssetIndex = ({ institutions, LinkComponent }) => {

    let covers = institutions.map((institution) => {
        let image = institution['logo-avatar']
        return (
            <LinkComponent to={"/asset-catalog/" + institution.id}>
                <div className="institution-cover">
                    <CBAssetImage className="asset" asset={image} width={256} height={256} />
                    <span>{institution.name ?? institution.id}</span>
                    <div className="border" />
                </div>
            </LinkComponent>
        )
    })

    return (
        <CBPage containerClassName={"content-builder-container-inset"}>
            <CBHeader><h1>Asset Catalog Demo</h1></CBHeader>
            <div className="institution-grid content-builder-container-inset">
                {covers}
            </div>
        </CBPage>
    )

}

const AppAssetIndexLoader = () => {
    const institutions = useLoaderData()
    return <AppAssetIndex institutions={institutions} LinkComponent={Link}/>
}

let routes = (root) => {
    console.log('creating routes');

    let route = [
        {
            path: "/asset-catalog",
            Component: Test,
            loader: indexLoader
        },
        {
            path: "/asset-catalog/:institutionid",
            Component: AppAssetCatalog,
            loader: loader
        }
    ]

    console.log('returning routes');

    return route
}

const Test = () => {
    return <div>{"Dave"}</div>
}

let internalRoutes = [
        {
            path: "/",
            Component: AppAssetIndexLoader,
            loader: indexLoader
        },
        {
            path: "/asset-catalog/:institutionid",
            Component: AppAssetCatalogLoader,
            loader: loader
        },
]

const router = createBrowserRouter(internalRoutes);

const App = () => {
    return <div>
        <RouterProvider router={router} />
    </div>
}


export { App, AppAssetIndex, AppAssetCatalog, routes }
